import type { Bounds, LatLng } from "@oshimap/domain";

/**
 * Web Mercator (EPSG:3857). Google · Naver · Kakao · OSM 이 모두 쓰는 투영이라,
 * 어떤 Provider 를 고르든(OD-003) 여기서 계산한 좌표가 그대로 맞는다.
 *
 * 등장방형(위경도를 그대로 x·y 로) 투영은 위도가 올라갈수록 가로가 늘어져
 * 서울 위도(37.5°)에서 이미 눈에 띄게 어긋난다. 실제 지도 위에 마커를 얹는 순간
 * 자리가 틀리므로, 지금부터 맞는 식을 쓴다.
 */
export const TILE_SIZE = 256;

/** Web Mercator 가 다룰 수 있는 위도 한계. 극지방은 무한대로 발산한다. */
export const MAX_LATITUDE = 85.05112878;

export function clampLatitude(lat: number): number {
  return Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, lat));
}

/** 위경도 → 월드 픽셀 (zoom 기준). 원점은 좌상단. */
export function project(p: LatLng, zoom: number): { x: number; y: number } {
  const scale = TILE_SIZE * Math.pow(2, zoom);
  const lat = clampLatitude(p.latitude);
  const sin = Math.sin((lat * Math.PI) / 180);
  return {
    x: scale * (0.5 + p.longitude / 360),
    y: scale * (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)),
  };
}

/** 월드 픽셀 → 위경도. */
export function unproject(pt: { x: number; y: number }, zoom: number): LatLng {
  const scale = TILE_SIZE * Math.pow(2, zoom);
  const longitude = (pt.x / scale - 0.5) * 360;
  const n = Math.PI * (1 - 2 * (pt.y / scale));
  const latitude = (180 / Math.PI) * Math.atan(Math.sinh(n));
  return { latitude, longitude };
}

/**
 * 화면에 실제로 보이는 영역.
 *
 * 컨테이너 크기를 넣지 않으면 "보이는 곳" 을 알 수 없다 —
 * 고정된 span 을 쓰면 세로로 긴 폰과 가로로 넓은 데스크톱이 같은 범위를 조회하고,
 * "이 지역" 버튼이 화면 밖의 장소를 가져오거나 화면 안의 장소를 빠뜨린다.
 */
export function viewportBounds(
  center: LatLng,
  zoom: number,
  size: { width: number; height: number },
): Bounds {
  const c = project(center, zoom);
  const nw = unproject({ x: c.x - size.width / 2, y: c.y - size.height / 2 }, zoom);
  const se = unproject({ x: c.x + size.width / 2, y: c.y + size.height / 2 }, zoom);
  return { north: nw.latitude, west: nw.longitude, south: se.latitude, east: se.longitude };
}

/** 화면 좌표(컨테이너 기준) 로 옮긴다. 마커를 어디에 그릴지. */
export function toScreen(
  p: LatLng,
  center: LatLng,
  zoom: number,
  size: { width: number; height: number },
): { x: number; y: number } {
  const c = project(center, zoom);
  const t = project(p, zoom);
  return { x: t.x - c.x + size.width / 2, y: t.y - c.y + size.height / 2 };
}

/** 화면 좌표 → 위경도. 드래그·클릭 위치를 지도 좌표로 되돌린다. */
export function fromScreen(
  pt: { x: number; y: number },
  center: LatLng,
  zoom: number,
  size: { width: number; height: number },
): LatLng {
  const c = project(center, zoom);
  return unproject({ x: c.x + pt.x - size.width / 2, y: c.y + pt.y - size.height / 2 }, zoom);
}

export interface TileRef { x: number; y: number; z: number }

/**
 * 화면을 덮는 타일 목록. Provider 가 무엇이든 XYZ 스킴은 같다.
 * 세로로는 범위를 벗어난 타일을 버리고, 가로로는 날짜변경선을 넘어 감싼다.
 */
export function tilesForViewport(
  center: LatLng,
  zoom: number,
  size: { width: number; height: number },
): { tiles: TileRef[]; originX: number; originY: number } {
  const z = Math.round(zoom);
  const c = project(center, z);
  const left = c.x - size.width / 2;
  const top = c.y - size.height / 2;

  const first = { x: Math.floor(left / TILE_SIZE), y: Math.floor(top / TILE_SIZE) };
  const last = {
    x: Math.floor((left + size.width) / TILE_SIZE),
    y: Math.floor((top + size.height) / TILE_SIZE),
  };

  const span = Math.pow(2, z);
  const tiles: TileRef[] = [];
  for (let y = first.y; y <= last.y; y += 1) {
    if (y < 0 || y >= span) continue;                 /* 위아래는 지도가 없다 */
    for (let x = first.x; x <= last.x; x += 1) {
      tiles.push({ x: ((x % span) + span) % span, y, z });  /* 좌우는 감싼다 */
    }
  }
  return { tiles, originX: first.x * TILE_SIZE - left, originY: first.y * TILE_SIZE - top };
}

/** `{z}/{x}/{y}` 치환. Provider 별 URL 모양이 달라도 이 세 값은 같다. */
export function tileUrl(template: string, t: TileRef): string {
  return template
    .replace("{z}", String(t.z))
    .replace("{x}", String(t.x))
    .replace("{y}", String(t.y));
}

/**
 * 화면 폭이 실제 거리로 `km` 가 되는 줌.
 *
 * 고정 줌을 쓰면 폰(390px)과 데스크톱(1140px)이 전혀 다른 넓이를 본다 —
 * 같은 화면에서 폰은 3km, 데스크톱은 9km 가 보인다. "내 주변" 이 기기마다 다른 뜻이 된다.
 * 보이는 **거리**를 맞추고 줌을 거기서 역산한다.
 */
export function zoomForWidthKm(km: number, widthPx: number, latitude: number): number {
  /* 적도에서 경도 1도 ≈ 111.32km, 위도가 올라가면 cos 만큼 짧아진다. */
  const kmPerDegree = 111.32 * Math.cos((clampLatitude(latitude) * Math.PI) / 180);
  const degrees = km / kmPerDegree;
  const zoom = Math.log2((widthPx * 360) / (TILE_SIZE * degrees));
  return Math.max(3, Math.min(19, zoom));
}

/**
 * 화면에 그릴 위경도 격자 간격(도).
 * 지도가 없는 동안 위치 감각을 주는 것이 목적이라, 화면에 5~10줄쯤 남는 간격을 고른다.
 */
export function graticuleStep(spanDegrees: number): number {
  const NICE = [0.0005, 0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10];
  const target = spanDegrees / 6;
  return NICE.find((n) => n >= target) ?? NICE[NICE.length - 1]!;
}

/** 눈금자에 쓸 "깔끔한" 거리(m)와 그 픽셀 길이. */
export function scaleBar(
  center: LatLng,
  zoom: number,
  maxPx = 96,
): { meters: number; px: number; label: string } {
  const metersPerPixel = (156543.03392 * Math.cos((clampLatitude(center.latitude) * Math.PI) / 180))
    / Math.pow(2, zoom);
  const NICE = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10_000, 20_000, 50_000, 100_000];
  const maxMeters = metersPerPixel * maxPx;
  const meters = [...NICE].reverse().find((n) => n <= maxMeters) ?? NICE[0]!;
  return {
    meters,
    px: meters / metersPerPixel,
    label: meters >= 1000 ? `${meters / 1000} km` : `${meters} m`,
  };
}
